/**
 * BYOK teardown banner (M1.9).
 *
 * Shown to tenants who still have a stored `ANTHROPIC_API_KEY` in
 * `tenant_secrets`. Self-clears once the operator runs the SQL DELETE
 * documented in `docs/runbooks/byok-teardown.md` — the secret disappears
 * from the list, the banner stops rendering.
 *
 * The dismiss button writes a localStorage flag so the banner doesn't
 * reappear on every page load while the user is making the
 * recommended change. Storage key is keyed by `?v=` so a future banner
 * revision (different copy / link) re-triggers visibility for users who
 * dismissed the prior version.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useSecretsList } from "@/hooks/use-secrets";

const DISMISS_KEY = "byok-teardown-banner-dismissed-v1";

export function ByokTeardownBanner() {
  const list = useSecretsList();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;
  if (list.isPending || list.isError) return null;

  const hasLegacyKey = list.data?.items.some(
    (s) => s.name === "ANTHROPIC_API_KEY" || s.name === "OPENAI_API_KEY",
  );
  if (!hasLegacyKey) return null;

  function handleDismiss() {
    try {
      window.localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // localStorage unavailable (private browsing); just hide for the session.
    }
    setDismissed(true);
  }

  return (
    <div
      role="status"
      data-testid="byok-teardown-banner"
      className="mx-7 mt-5 flex items-start justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <div className="flex-1">
        <p className="font-semibold">Your stored AI provider key is no longer used</p>
        <p className="mt-1 text-amber-800">
          Relay now provides Anthropic and OpenAI access directly under your subscription — you no
          longer need to bring your own key. Your stored <code className="font-mono">{" "}
          {list.data?.items.find((s) => s.name === "OPENAI_API_KEY")
            ? "OPENAI_API_KEY / ANTHROPIC_API_KEY"
            : "ANTHROPIC_API_KEY"}{" "}</code>{" "}
          will be removed shortly. No action required; runs continue with the platform key.{" "}
          <Link to="/settings/billing" className="font-medium underline">
            See your credits
          </Link>{" "}
          on the Billing page.
        </p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleDismiss}
        aria-label="Dismiss notice"
        className="-mr-2 h-7 w-7 p-0 text-amber-900 hover:bg-amber-100"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
